import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes, FaStar, FaArrowRight, FaFilter } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const Comparisons: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const bookmakers = [
    {
      name: 'Winamax',
      logo: '/images/offers/winamax.webp',
      url: 'https://www.winamax.fr/',
      rating: 4.8,
      bonus: '150€',
      minDeposit: '10€',
      payoutSpeed: '12h',
      mobileApp: true,
      liveBetting: true,
      cashOut: true,
      streaming: false,
      license: 'ANJ',
    },
    {
      name: 'Bwin',
      logo: '/images/offers/bwin.webp',
      url: 'https://bwin.fr/',
      rating: 4.7,
      bonus: '100€',
      minDeposit: '10€',
      payoutSpeed: '24h',
      mobileApp: true,
      liveBetting: true,
      cashOut: true,
      streaming: true,
      license: 'ANJ',
    },
    {
      name: 'Unibet',
      logo: '/images/offers/unibet.webp',
      url: 'https://www.unibet.fr/',
      rating: 4.6,
      bonus: '120€',
      minDeposit: '10€',
      payoutSpeed: '24h',
      mobileApp: true,
      liveBetting: true,
      cashOut: true,
      streaming: true,
      license: 'ANJ',
    },
  ];

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'bonus', label: 'Meilleur Bonus' },
    { id: 'speed', label: 'Retraits Rapides' },
    { id: 'features', label: 'Fonctionnalités' },
  ];

  const filteredBookmakers = bookmakers.sort((a, b) => {
    if (selectedCategory === 'bonus') {
      const bonusA = parseInt(a.bonus.replace('€', ''));
      const bonusB = parseInt(b.bonus.replace('€', ''));
      return bonusB - bonusA;
    }
    if (selectedCategory === 'speed') {
      const speedA = parseInt(a.payoutSpeed.replace('h', ''));
      const speedB = parseInt(b.payoutSpeed.replace('h', ''));
      return speedA - speedB;
    }
    if (selectedCategory === 'features') {
      const featuresA = [a.mobileApp, a.liveBetting, a.cashOut, a.streaming].filter(Boolean).length;
      const featuresB = [b.mobileApp, b.liveBetting, b.cashOut, b.streaming].filter(Boolean).length;
      return featuresB - featuresA;
    }
    return b.rating - a.rating;
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Table',
    about: 'Comparaison des Bookmakers Français',
    name: 'Tableau Comparatif des Meilleurs Bookmakers',
    description: 'Comparez facilement les caractéristiques, bonus et fonctionnalités des meilleurs bookmakers français.',
  };

  return (
    <Layout>
      <SEO
        title="Comparaison Détaillée des Bookmakers Français"
        description="Comparez en un coup d'œil les meilleurs bookmakers français. Bonus, fonctionnalités, retraits et cotes comparés pour vous aider à choisir."
        url="/comparisons"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">Comparaison</span> des Bookmakers
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Comparez facilement les meilleurs bookmakers français. Tous les critères importants en un seul regard.
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'glass text-gray-300 hover:bg-white/20'
                }`}
              >
                <FaFilter className="inline mr-2" />
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="glass rounded-3xl p-6 md:p-8 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 font-bold text-lg">Bookmaker</th>
                  <th className="text-center py-4 px-4 font-bold">Note</th>
                  <th className="text-center py-4 px-4 font-bold">Bonus</th>
                  <th className="text-center py-4 px-4 font-bold">Dépôt min.</th>
                  <th className="text-center py-4 px-4 font-bold">Retrait</th>
                  <th className="text-center py-4 px-4 font-bold">App Mobile</th>
                  <th className="text-center py-4 px-4 font-bold">Live</th>
                  <th className="text-center py-4 px-4 font-bold">Cash-out</th>
                  <th className="text-center py-4 px-4 font-bold">Streaming</th>
                  <th className="text-center py-4 px-4 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookmakers.map((bookmaker, index) => (
                  <tr
                    key={bookmaker.name}
                    className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                      index % 2 === 0 ? 'bg-white/5' : ''
                    }`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <img 
                          src={bookmaker.logo} 
                          alt={bookmaker.name}
                          className="h-10 w-[100px] object-contain"
                          width="100"
                          height="40"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="font-bold text-lg">{bookmaker.name}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <FaStar className="text-yellow-400" />
                        <span className="font-bold">{bookmaker.rating}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="font-bold text-gradient">{bookmaker.bonus}</span>
                    </td>
                    <td className="py-4 px-4 text-center">{bookmaker.minDeposit}</td>
                    <td className="py-4 px-4 text-center">{bookmaker.payoutSpeed}</td>
                    <td className="py-4 px-4 text-center">
                      {bookmaker.mobileApp ? (
                        <FaCheck className="text-green-400 mx-auto" />
                      ) : (
                        <FaTimes className="text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {bookmaker.liveBetting ? (
                        <FaCheck className="text-green-400 mx-auto" />
                      ) : (
                        <FaTimes className="text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {bookmaker.cashOut ? (
                        <FaCheck className="text-green-400 mx-auto" />
                      ) : (
                        <FaTimes className="text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {bookmaker.streaming ? (
                        <FaCheck className="text-green-400 mx-auto" />
                      ) : (
                        <FaTimes className="text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <a
                        href={bookmaker.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 px-4 py-2 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-lg text-sm font-semibold text-white hover:shadow-lg hover:shadow-purple-500/50 transition-all"
                      >
                        <span>Visiter</span>
                        <FaArrowRight className="text-xs" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="glass rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">150€</div>
              <div className="text-gray-400">Meilleur Bonus</div>
              <div className="text-sm text-gray-500 mt-1">Winamax</div>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">12h</div>
              <div className="text-gray-400">Retrait le plus rapide</div>
              <div className="text-sm text-gray-500 mt-1">Winamax</div>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-gradient mb-2">4.8/5</div>
              <div className="text-gray-400">Meilleure Note</div>
              <div className="text-sm text-gray-500 mt-1">Winamax</div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Comparisons;

